import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  addHours,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isPast,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  setHours,
  startOfHour,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR, es, enUS } from "date-fns/locale";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TaskModal from "../../components/TaskModal";
import ScheduleModal from "../../components/ScheduleModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const COLORS = {
  task: "#6D28D9",
  taskDone: "#9CA3AF",
  schedule: "#4F46E5",
  scheduleSent: "#16A34A",
  error: "#DC2626",
};

const useStyles = makeStyles((theme) => ({
  layout: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      overflowY: "auto",
    },
  },
  calendar: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5),
    borderRadius: theme.borderRadius.medium,
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  monthTitle: {
    flex: 1,
    fontWeight: 600,
    textTransform: "capitalize",
  },
  weekdays: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    borderBottom: theme.border.divider,
  },
  weekday: {
    padding: theme.spacing(0.5),
    textAlign: "center",
    ...theme.typography.sectionLabel,
  },
  grid: {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gridAutoRows: "minmax(92px, 1fr)",
  },
  day: {
    minWidth: 0,
    padding: 4,
    borderRight: theme.border.divider,
    borderBottom: theme.border.divider,
    cursor: "pointer",
    overflow: "hidden",
    "&:nth-child(7n)": {
      borderRight: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.tintedBackground,
    },
  },
  outside: {
    opacity: 0.45,
  },
  selectedDay: {
    backgroundColor: theme.palette.action.selected,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  dayNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    marginBottom: 2,
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    fontWeight: 500,
  },
  today: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  event: {
    display: "block",
    marginBottom: 2,
    padding: "0 6px",
    borderRadius: 4,
    color: "#FFF",
    fontSize: 11,
    lineHeight: "18px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  more: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    paddingLeft: 4,
  },
  side: {
    width: 320,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    borderRadius: theme.borderRadius.medium,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      overflowY: "visible",
    },
  },
  sideTitle: {
    fontWeight: 600,
    "&::first-letter": {
      textTransform: "uppercase",
    },
    marginBottom: theme.spacing(1),
  },
  sideActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  item: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: theme.borderRadius.small,
    borderLeft: "4px solid",
    backgroundColor: theme.palette.tintedBackground,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  itemIcon: {
    fontSize: 18,
    marginTop: 2,
    color: theme.palette.text.secondary,
  },
  itemBody: {
    minWidth: 0,
  },
  itemMeta: {
    color: theme.palette.text.secondary,
  },
  doneText: {
    textDecoration: "line-through",
  },
  empty: {
    color: theme.palette.text.secondary,
    ...theme.typography.body2,
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
  },
  legendDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    marginRight: 4,
    borderRadius: 2,
    verticalAlign: "middle",
  },
}));

const locales = { pt: ptBR, es, en: enUS };
const dateLocale = () => locales[(i18n.language || "pt").slice(0, 2)] || ptBR;

const MAX_PER_DAY = 3;

const taskColor = (task) => {
  if (task.status === "done") return COLORS.taskDone;
  if (isPast(parseISO(task.dueAt))) return COLORS.error;
  return COLORS.task;
};

const scheduleColor = (schedule) => {
  if (schedule.status === "sent") return COLORS.scheduleSent;
  if (schedule.status === "error") return COLORS.error;
  return COLORS.schedule;
};

// Horário sugerido ao criar algo num dia: 9h, ou a próxima hora cheia se for hoje
const suggestedTime = (day) =>
  isToday(day) ? startOfHour(addHours(new Date(), 1)) : setHours(day, 9);

const Calendar = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [taskModal, setTaskModal] = useState({ open: false });
  const [scheduleModal, setScheduleModal] = useState({ open: false });

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month)),
        end: endOfWeek(endOfMonth(month)),
      }),
    [month]
  );

  const fetchEvents = useCallback(async () => {
    const params = {
      dateFrom: days[0].toISOString(),
      dateTo: endOfWeek(days[days.length - 1]).toISOString(),
      pageSize: 500,
    };
    try {
      const [tasksRes, schedulesRes] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/schedules", { params }),
      ]);
      setTasks(tasksRes.data.tasks);
      setSchedules(schedulesRes.data.schedules);
    } catch (err) {
      toastError(err);
    }
  }, [days]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("task", fetchEvents);
    socket.on("schedule", fetchEvents);
    return () => socket.disconnect();
  }, [fetchEvents]);

  const eventsOf = useCallback(
    (day) => {
      const dayTasks = tasks
        .filter((t) => isSameDay(parseISO(t.dueAt), day))
        .map((t) => ({
          type: "task",
          id: t.id,
          at: parseISO(t.dueAt),
          title: t.title,
          color: taskColor(t),
          meta: t.assignedUser?.name,
          done: t.status === "done",
          item: t,
        }));
      const daySchedules = schedules
        .filter((s) => isSameDay(parseISO(s.sendAt), day))
        .map((s) => ({
          type: "schedule",
          id: s.id,
          at: parseISO(s.sendAt),
          title: s.contact?.name,
          color: scheduleColor(s),
          meta: s.body,
          done: s.status === "sent",
          item: s,
        }));
      return [...dayTasks, ...daySchedules].sort((a, b) => a.at - b.at);
    },
    [tasks, schedules]
  );

  const openEvent = (event) => {
    if (event.type === "task") {
      setTaskModal({ open: true, taskId: event.id });
      return;
    }
    const s = event.item;
    const editable =
      ["pending", "error"].includes(s.status) &&
      (user.profile === "admin" || s.userId === user.id);
    if (editable) setScheduleModal({ open: true, scheduleId: s.id });
  };

  const selectedEvents = eventsOf(selectedDay);
  const locale = dateLocale();
  const weekdays = i18n.t("calendar.weekdays").split(",");

  return (
    <MainContainer>
      <TaskModal
        open={taskModal.open}
        taskId={taskModal.taskId}
        initialDueAt={taskModal.dueAt}
        onClose={() => setTaskModal({ open: false })}
      />
      <ScheduleModal
        open={scheduleModal.open}
        scheduleId={scheduleModal.scheduleId}
        initialSendAt={scheduleModal.sendAt}
        onClose={() => setScheduleModal({ open: false })}
      />
      <MainHeader>
        <Title>{i18n.t("calendar.title")}</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>
      <div className={classes.layout}>
        <Paper variant="outlined" className={classes.calendar}>
          <div className={classes.nav}>
            <IconButton size="small" onClick={() => setMonth(subMonths(month, 1))}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" onClick={() => setMonth(addMonths(month, 1))}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="h6" className={classes.monthTitle}>
              {format(month, "MMMM yyyy", { locale })}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => {
                setMonth(startOfMonth(new Date()));
                setSelectedDay(new Date());
              }}
            >
              {i18n.t("calendar.today")}
            </Button>
          </div>
          <div className={classes.weekdays}>
            {weekdays.map((d) => (
              <div key={d} className={classes.weekday}>
                {d}
              </div>
            ))}
          </div>
          <div className={classes.grid}>
            {days.map((day) => {
              const events = eventsOf(day);
              return (
                <div
                  key={day.toISOString()}
                  className={clsx(
                    classes.day,
                    !isSameMonth(day, month) && classes.outside,
                    isSameDay(day, selectedDay) && classes.selectedDay
                  )}
                  onClick={() => setSelectedDay(day)}
                >
                  <span className={clsx(classes.dayNumber, isToday(day) && classes.today)}>
                    {format(day, "d")}
                  </span>
                  {events.slice(0, MAX_PER_DAY).map((event) => (
                    <span
                      key={`${event.type}-${event.id}`}
                      className={classes.event}
                      style={{ backgroundColor: event.color }}
                      title={event.title}
                    >
                      {format(event.at, "HH:mm")} {event.title}
                    </span>
                  ))}
                  {events.length > MAX_PER_DAY && (
                    <span className={classes.more}>
                      {i18n.t("calendar.more", { count: events.length - MAX_PER_DAY })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className={classes.legend}>
            {Object.keys(COLORS).map((key) => (
              <span key={key}>
                <span className={classes.legendDot} style={{ backgroundColor: COLORS[key] }} />
                {i18n.t(`calendar.legend.${key}`)}
              </span>
            ))}
          </div>
        </Paper>

        <Paper variant="outlined" className={classes.side}>
          <Typography variant="subtitle1" className={classes.sideTitle}>
            {format(selectedDay, "PPPP", { locale })}
          </Typography>
          <div className={classes.sideActions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() =>
                setTaskModal({ open: true, dueAt: suggestedTime(selectedDay) })
              }
            >
              {i18n.t("calendar.newTask")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() =>
                setScheduleModal({ open: true, sendAt: suggestedTime(selectedDay) })
              }
            >
              {i18n.t("calendar.newSchedule")}
            </Button>
          </div>
          {selectedEvents.length === 0 && (
            <div className={classes.empty}>{i18n.t("calendar.noEvents")}</div>
          )}
          {selectedEvents.map((event) => {
            const Icon =
              event.type === "task" ? AssignmentTurnedInOutlinedIcon : ScheduleOutlinedIcon;
            return (
              <div
                key={`${event.type}-${event.id}`}
                className={classes.item}
                style={{ borderLeftColor: event.color }}
                onClick={() => openEvent(event)}
              >
                <Icon className={classes.itemIcon} />
                <div className={classes.itemBody}>
                  <Typography
                    variant="body2"
                    noWrap
                    className={clsx(event.done && classes.doneText)}
                  >
                    {format(event.at, "HH:mm")} · {event.title}
                  </Typography>
                  {event.meta && (
                    <Typography variant="caption" component="div" noWrap className={classes.itemMeta}>
                      {event.meta}
                    </Typography>
                  )}
                </div>
              </div>
            );
          })}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Calendar;
